import React from 'react';
import { getCurrency } from '../utils/currency';

// ---- Imperative confirm + toast (replaces window.confirm / alert) -------
let _confirm = null, _toast = null;
export function confirmDialog(opts) {
  const o = typeof opts === 'string' ? { message: opts } : (opts || {});
  if (_confirm) return _confirm(o);
  return Promise.resolve(window.confirm(o.message || 'Are you sure?'));
}
export function promptDialog(opts) {
  const o = typeof opts === 'string' ? { message: opts } : (opts || {});
  o.input = true;
  if (_confirm) return _confirm(o);
  return Promise.resolve(window.prompt(o.message || '', o.defaultValue || ''));
}
export function notify(message, type = 'info') {
  if (_toast) return _toast({ message: String(message), type });
  if (type === 'error') console.error(message); else console.log(message);
}
export function ConfirmRoot() {
  const [c, setC] = React.useState(null);
  const [val, setVal] = React.useState('');
  const [toasts, setToasts] = React.useState([]);
  React.useEffect(() => {
    _confirm = (o) => new Promise(res => { setVal(o.defaultValue != null ? String(o.defaultValue) : ''); setC({ o, res }); });
    _toast = (t) => {
      const id = Math.random().toString(36).slice(2);
      setToasts(ts => [...ts, { id, ...t }]);
      setTimeout(() => setToasts(ts => ts.filter(x => x.id !== id)), 3500);
    };
    return () => { _confirm = null; _toast = null; };
  }, []);
  const isInput = c && c.o.input;
  const done = (ok) => { if (!c) return; c.res(isInput ? (ok ? val : null) : ok); setC(null); };
  const danger = c && c.o.danger;
  const tColor = t => t.type === 'error' ? '#ef4444' : t.type === 'success' ? '#22c55e' : 'var(--primary)';
  return (
    <>
      {c && (
        <div onClick={() => done(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} className="card-dark" style={{ width: 420, maxWidth: '100%', padding: 22 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)', marginBottom: 8 }}>{c.o.title || (isInput ? 'Enter value' : 'Please confirm')}</div>
            <div style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: isInput ? 12 : 20, whiteSpace: 'pre-line' }}>{c.o.message}</div>
            {isInput && (
              <input autoFocus type={c.o.inputType || 'text'} value={val} placeholder={c.o.placeholder || ''}
                onChange={e => setVal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') done(true); }}
                style={{ width: '100%', background: 'var(--dark4)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 8, padding: '10px 12px', fontSize: 14, marginBottom: 20 }} />
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => done(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{c.o.cancelText || 'Cancel'}</button>
              <button onClick={() => done(true)} style={{ background: danger ? '#ef4444' : 'var(--primary)', border: 'none', color: '#fff', borderRadius: 8, padding: '9px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>{c.o.confirmText || (isInput ? 'OK' : danger ? 'Delete' : 'Confirm')}</button>
            </div>
          </div>
        </div>
      )}
      {toasts.length > 0 && (
        <div style={{ position: 'fixed', top: 18, right: 18, zIndex: 2100, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360 }}>
          {toasts.map(t => (
            <div key={t.id} className="card-dark" style={{ padding: '12px 16px', borderLeft: `4px solid ${tColor(t)}`, fontSize: 13, color: 'var(--text)', boxShadow: '0 8px 24px rgba(0,0,0,.25)' }}>
              {t.message}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---- Themed chart tooltip (readable in BOTH light & dark) ---------------
const tipWrap = { background: 'var(--dark3)', border: '1px solid var(--primary)', borderRadius: 10, padding: '9px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', fontSize: 12 };
export function ChartTooltip({ active, payload, label, prefix = '', money = false, withPct = false }) {
  if (!active || !payload || !payload.length) return null;
  const total = withPct ? payload.reduce((a, p) => a + (Number(p.value) || 0), 0) : 0;
  return (
    <div style={tipWrap}>
      {label != null && <div style={{ color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>{label}</div>}
      {payload.map((p, i) => {
        const v = Number(p.value) || 0;
        const val = money ? `${getCurrency()}${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : v;
        const pct = withPct && total > 0 ? ` · ${((v / total) * 100).toFixed(1)}%` : '';
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--text)' }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: p.color || p.payload?.fill || 'var(--primary)' }} />
            <span style={{ color: 'var(--text-muted)' }}>{p.name || prefix}</span>
            <b style={{ marginLeft: 'auto', color: 'var(--text)' }}>{val}{pct}</b>
          </div>
        );
      })}
    </div>
  );
}

// ---- Modal --------------------------------------------------------------
export function Modal({ title, onClose, children, footer, wide }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '5vh 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} className="card-dark" style={{ width: wide ? 720 : 460, maxWidth: '100%', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {children}
        {footer && <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>{footer}</div>}
      </div>
    </div>
  );
}

// ---- Searchable single-select dropdown ---------------------------------
export function SearchableSelect({ value, onChange, options = [], placeholder = 'Select…', style, disabled }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [rect, setRect] = React.useState(null);
  const ref = React.useRef(null);
  const triggerRef = React.useRef(null);
  const reposition = React.useCallback(() => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setRect({ left: r.left, top: r.bottom + 4, width: r.width, bottom: r.bottom });
    }
  }, []);
  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  React.useEffect(() => {
    if (!open) return;
    reposition();
    const onScroll = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); };
  }, [open, reposition]);
  const sel = options.find(o => String(o.value) === String(value));
  const ql = q.trim().toLowerCase();
  const filtered = ql ? options.filter(o => String(o.label).toLowerCase().includes(ql)) : options;
  // Drop upward if not enough space below.
  const spaceBelow = rect ? window.innerHeight - rect.top : 9999;
  const dropUp = rect && spaceBelow < 280 && rect.top > 320;
  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <div ref={triggerRef} onClick={() => { if (!disabled) { setOpen(o => !o); setQ(''); } }} className="form-control-dark"
        style={{ width: '100%', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, boxSizing: 'border-box' }}>
        <span style={{ color: sel ? 'var(--text)' : 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sel ? sel.label : placeholder}</span>
        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>▾</span>
      </div>
      {open && rect && (
        <div className="card-dark" style={{
          position: 'fixed', left: rect.left, width: rect.width, zIndex: 4000, padding: 6, maxHeight: 260, overflowY: 'auto', boxShadow: '0 10px 28px rgba(0,0,0,.35)',
          ...(dropUp ? { bottom: window.innerHeight - rect.bottom + 4 + (triggerRef.current ? triggerRef.current.offsetHeight : 0) } : { top: rect.top }),
        }}>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search…"
            style={{ width: '100%', background: 'var(--dark4)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 6, padding: '7px 9px', fontSize: 13, marginBottom: 6, boxSizing: 'border-box' }} />
          {filtered.length === 0 && <div style={{ padding: '8px 9px', fontSize: 12, color: 'var(--text-muted)' }}>No matches</div>}
          {filtered.map(o => {
            const on = String(o.value) === String(value);
            return (
              <div key={String(o.value)} onClick={() => { onChange(o.value); setOpen(false); }}
                style={{ padding: '8px 9px', fontSize: 13, borderRadius: 6, cursor: 'pointer', background: on ? 'var(--primary)' : 'transparent', color: on ? '#fff' : 'var(--text)' }}
                onMouseEnter={e => { if (!on) e.currentTarget.style.background = 'var(--dark4)'; }}
                onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent'; }}>
                {o.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Drop-in <Select> replacement for native <select> (searchable) ------
function _flattenText(node) {
  if (node == null || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(_flattenText).join('');
  if (React.isValidElement(node)) return _flattenText(node.props.children);
  return '';
}
function _collectOptions(children, acc) {
  React.Children.forEach(children, child => {
    if (child == null || child === false || child === true) return;
    if (Array.isArray(child)) { _collectOptions(child, acc); return; }
    if (!React.isValidElement(child)) return;
    if (child.type === 'option') {
      const label = _flattenText(child.props.children);
      const value = child.props.value !== undefined ? child.props.value : label;
      acc.push({ value, label });
    } else {
      _collectOptions(child.props.children, acc);
    }
  });
}
export function Select({ value, onChange, children, style, disabled, placeholder }) {
  const opts = [];
  _collectOptions(children, opts);
  const ph = placeholder || (opts.find(o => String(o.value) === '') || {}).label || 'Select…';
  return (
    <SearchableSelect value={value} disabled={disabled} placeholder={ph} style={style}
      options={opts}
      onChange={v => { if (onChange) onChange({ target: { value: v } }); }} />
  );
}

// ---- Category chip bar --------------------------------------------------
// One scrollable row (with ‹ › arrows) so orgs with many categories don't eat the
// screen; the ▾ toggle expands to the full wrapped list. counts = { cat: n }.
export function CategoryBar({ categories, value, onChange, counts }) {
  const ref = React.useRef(null);
  const [expanded, setExpanded] = React.useState(false);
  const [edges, setEdges] = React.useState({ left: false, right: false });

  const measure = React.useCallback(() => {
    const el = ref.current; if (!el) return;
    setEdges({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
  }, []);
  React.useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, categories, expanded]);
  // Keep the selected chip visible when it changes.
  React.useEffect(() => {
    const el = ref.current && ref.current.querySelector('[data-active="1"]');
    if (el && !expanded) el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [value, expanded]);

  const scrollBy = (dir) => { const el = ref.current; if (el) el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' }); };
  const arrow = (dir, show) => (
    <button type="button" onClick={() => scrollBy(dir)} disabled={!show} aria-label={dir < 0 ? 'Scroll left' : 'Scroll right'}
      style={{ flexShrink: 0, width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--dark2, transparent)',
        color: 'var(--text-muted)', cursor: show ? 'pointer' : 'default', opacity: show ? 1 : 0.35, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <i className={`bi bi-chevron-${dir < 0 ? 'left' : 'right'}`}></i>
    </button>
  );

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: expanded ? 'flex-start' : 'center', marginBottom: 16, width: '100%', minWidth: 0, contain: 'inline-size' }}>
      {!expanded && arrow(-1, edges.left)}
      <div ref={ref} onScroll={measure} className="cat-strip"
        style={{ flex: 1, minWidth: 0, display: 'flex', gap: 8, flexWrap: expanded ? 'wrap' : 'nowrap', overflowX: expanded ? 'visible' : 'auto',
          maxHeight: expanded ? 180 : undefined, overflowY: expanded ? 'auto' : 'hidden', scrollbarWidth: 'none' }}>
        {categories.map(c => {
          const on = value === c;
          return (
            <button key={c} type="button" data-active={on ? '1' : '0'} onClick={() => { onChange(c); setExpanded(false); }}
              style={{ flexShrink: 0, whiteSpace: 'nowrap', background: on ? 'var(--primary)' : 'transparent', border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                color: on ? '#fff' : 'var(--text-muted)', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: on ? 600 : 400 }}>
              {c}{counts && counts[c] != null && <span style={{ marginLeft: 6, opacity: 0.7, fontSize: 11 }}>{counts[c]}</span>}
            </button>
          );
        })}
      </div>
      {!expanded && arrow(1, edges.right)}
      <button type="button" onClick={() => setExpanded(x => !x)} title={expanded ? 'Collapse' : 'Show all categories'}
        style={{ flexShrink: 0, height: 30, borderRadius: 15, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)',
          cursor: 'pointer', padding: '0 10px', fontSize: 12, whiteSpace: 'nowrap' }}>
        <i className={`bi bi-${expanded ? 'chevron-up' : 'grid-3x3-gap'} me-1`}></i>{expanded ? 'Less' : categories.length}
      </button>
    </div>
  );
}

export function IconBtn({ icon, color = 'var(--text-muted)', title, onClick }) {
  return (
    <button title={title} onClick={onClick} className="icon-btn" style={{ color }}>
      <i className={`bi ${icon}`}></i>
    </button>
  );
}

// ---- Client-side pagination hook ---------------------------------------
export function usePaged(list, initialSize = 10) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(initialSize);
  const total = list.length;
  const showAll = pageSize === 'all';
  const eff = showAll ? Math.max(total, 1) : pageSize;
  const pages = Math.max(1, Math.ceil(total / eff));
  React.useEffect(() => { if (page > pages) setPage(1); }, [pages]); // eslint-disable-line
  const start = (page - 1) * eff;
  return { pageItems: showAll ? list : list.slice(start, start + eff), page, setPage, total, pages, pageSize, setPageSize };
}

// ---- Pagination control -------------------------------------------------
export function Pagination({ page, pages, total, pageSize, onPage, onPageSize, sizeOptions = [10, 25, 50, 100] }) {
  if (!total) return null;
  const showAll = pageSize === 'all';
  const eff = showAll ? total : pageSize;
  const from = showAll ? 1 : (page - 1) * eff + 1, to = showAll ? total : Math.min(total, page * eff);
  const opts = [...new Set([...(typeof pageSize === 'number' ? [pageSize] : []), ...sizeOptions])].sort((a, b) => a - b);
  const nums = [];
  if (!showAll) {
    for (let p = 1; p <= pages; p++) {
      if (p === 1 || p === pages || Math.abs(p - page) <= 1) nums.push(p);
      else if (nums[nums.length - 1] !== '…') nums.push('…');
    }
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 4px', flexWrap: 'wrap' }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{from}–{to} of {total}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {onPageSize && (
          <select value={pageSize} onChange={e => { const v = e.target.value === 'all' ? 'all' : Number(e.target.value); onPageSize(v); onPage && onPage(1); }}
            className="form-select-dark" style={{ padding: '5px 8px', borderRadius: 8, fontSize: 12, marginRight: 4 }} title="Rows per page">
            {opts.map(s => <option key={s} value={s}>{s} / page</option>)}
            <option value="all">Show all</option>
          </select>
        )}
        {!showAll && <>
          <button className="pg-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}><i className="bi bi-chevron-left"></i></button>
          {nums.map((n, i) => n === '…'
            ? <span key={'e' + i} style={{ color: 'var(--text-muted)', padding: '0 4px' }}>…</span>
            : <button key={n} className={`pg-btn${n === page ? ' active' : ''}`} onClick={() => onPage(n)}>{n}</button>)}
          <button className="pg-btn" disabled={page >= pages} onClick={() => onPage(page + 1)}><i className="bi bi-chevron-right"></i></button>
        </>}
      </div>
    </div>
  );
}
